import { GridItemSize } from 'modules/desktop/constants'
import { BaseGridItem } from 'shared/types/desktop-grid'

import { findFirstFreePosition } from './findFirstFreePosition'
import { wouldCollide } from './wouldCollide'

type GridPosition = { row: number; col: number }

/**
 * Computes drop slots for `count` incoming shortcuts anchored at `anchor`.
 *
 * Items flow in reading order from the drop point: each row is filled to the
 * right — skipping cells blocked by existing items — and only wraps to the next
 * row band (beneath the anchor) once the row is exhausted. This keeps the group
 * flowing left-to-right past an obstacle instead of dropping an item directly
 * below it while space to its right is still open, so the layout stays
 * predictable regardless of which cell you aim at.
 *
 * If forward space runs out (a crowded desktop) the remaining items fall back
 * to the first free gap anywhere. Returns fewer than `count` positions only if
 * the grid genuinely has no room left.
 */
export const computeShortcutGroupTargets = (
  anchor: GridPosition,
  count: number,
  cols: number,
  rows: number,
  items: BaseGridItem[],
  size: GridItemSize
): GridPosition[] => {
  if (count <= 0) return []

  const maxRow = rows - size.height
  const maxCol = cols - size.width
  if (maxRow < 0 || maxCol < 0) return []

  const startCol = Math.min(Math.max(0, anchor.col), maxCol)
  const startRow = Math.min(Math.max(0, anchor.row), maxRow)

  const placed: BaseGridItem[] = []
  const targets: GridPosition[] = []

  const occupy = (position: GridPosition) => {
    targets.push(position)
    placed.push({
      id: `__flow-${targets.length}`,
      itemType: 'shortcut',
      position,
      size
    })
  }

  let row = startRow
  let col = startCol
  while (targets.length < count && row <= maxRow) {
    if (!wouldCollide('__flow', { row, col }, size, [...items, ...placed])) {
      occupy({ row, col })
    }
    col += size.width
    if (col > maxCol) {
      col = startCol
      row += size.height
    }
  }

  // Crowded desktop: no forward space left, so fill any remaining gap anywhere.
  while (targets.length < count) {
    const fallback = findFirstFreePosition(
      [...items, ...placed],
      cols,
      rows,
      size
    )
    if (!fallback) break
    occupy(fallback)
  }

  return targets
}
